import { Reducer } from 'redux';
import {
  ACTION, ClearBowtieData, RequestBowtie, ReceiveBowtie, FailBowtie,
  RequestDraft, ReceiveDraft, FailDraft,
  RequestConnectDraft, ReceiveConnectDraft, FailConnectDraft,
  RequestCase, ReceiveCase, FailCase,
  RequestConnectedCases, ReceiveConnectedCases, FailConnectedCases,
  CleanData,
  RequestBowtieFavourite,
  ReceiveBowtieFavourite
} from './action-types';
import { BowtieState } from './types-store';

export const INITIAL_BOWTIE_STATE: BowtieState = {
  currentCaseNumber: undefined,
  currentDraftId: undefined,
  fetchingBowtie: false,
  caseDataModel: undefined,
  bowtieLayout: undefined,
  caseTemplate: undefined,
  fetchingDraft: false,
  draftRequestId: 0,
  draftData: undefined,
  fetchingConnections: [],
  casesRequiringUpdate: [],
  connectionTypes: undefined,
  inspectionEvaluations: {
    fetching: [],
    evaluationStatuses: {}
  },
  connectedCases: {
    fetching: {},
    connected: {}
  },
  creatingBowtieFavourite: false,
  bowtieFavouriteId: undefined,
  requestId: 0
};

const reducers: { [indexer: string]: Reducer<BowtieState> } = {

  [ACTION.CLEAR_BOWTIE_DATA]: (state: BowtieState, action: ClearBowtieData): BowtieState => {

    return {
      ...state,
      currentCaseNumber: undefined,
      currentDraftId: undefined,
      fetchingBowtie: false,
      caseDataModel: undefined,
      bowtieLayout: undefined,
      caseTemplate: undefined,
      fetchingDraft: false,
      draftData: undefined,
      fetchingConnections: [],
      casesRequiringUpdate: [],
      connectionTypes: undefined,
      inspectionEvaluations: {
        fetching: [],
        evaluationStatuses: {}
      },
      connectedCases: {
        fetching: {},
        connected: {}
      }
    };
  },

  [ACTION.REQUEST_BOWTIE]: (state: BowtieState, action: RequestBowtie): BowtieState => {

    return {
      ...state,
      currentCaseNumber: action.caseNumber,
      currentDraftId: action.draftId,
      fetchingBowtie: true
    };
  },

  [ACTION.RECEIVE_BOWTIE]: (state: BowtieState, action: ReceiveBowtie): BowtieState => {

    if (state.currentCaseNumber !== action.caseNumber ||
      state.currentDraftId !== action.draftId) {
      return state;
    }

    return {
      ...state,
      fetchingBowtie: false,
      caseDataModel: action.caseDataModel,
      bowtieLayout: action.bowtieLayout,
      caseTemplate: action.caseTemplate,
      inspectionEvaluations: {
        ...state.inspectionEvaluations,
        evaluationStatuses: {
          ...state.inspectionEvaluations.evaluationStatuses,
          ...action.evaluations
        }
      }
    };
  },

  [ACTION.FAIL_BOWTIE]: (state: BowtieState, action: FailBowtie): BowtieState => {
    if (state.currentCaseNumber !== action.caseNumber ||
      state.currentDraftId !== action.draftId) {
      return state;
    }

    return {
      ...state,
      fetchingBowtie: false,
      caseDataModel: undefined,
      caseTemplate: undefined
    };
  },

  [ACTION.REQUEST_DRAFT]: (state: BowtieState, action: RequestDraft): BowtieState => {

    return {
      ...state,
      fetchingDraft: true,
      draftRequestId: action.requestId
    };
  },

  [ACTION.RECEIVE_DRAFT]: (state: BowtieState, action: ReceiveDraft): BowtieState => {
    return state.draftRequestId === action.requestId ? {
      ...state,
      fetchingDraft: false,
      draftData: action.draftData,
      connectionTypes: action.connectionTypes,
      casesRequiringUpdate: state.casesRequiringUpdate
        .filter(caseNumber => state.fetchingConnections.includes(caseNumber))
    } : state;
  },

  [ACTION.FAIL_DRAFT]: (state: BowtieState, action: FailDraft): BowtieState => {
    return state.draftRequestId === action.requestId ? {
      ...state,
      fetchingDraft: false,
      casesRequiringUpdate: state.casesRequiringUpdate
        .filter(caseNumber => state.fetchingConnections.includes(caseNumber))
    } : state;
  },

  [ACTION.REQUEST_CASE]: (state: BowtieState, action: RequestCase): BowtieState => {

    return {
      ...state,
      inspectionEvaluations: {
        ...state.inspectionEvaluations,
        fetching: [
          ...state.inspectionEvaluations.fetching,
          action.caseNumber
        ]
      }
    };
  },

  [ACTION.RECEIVE_CASE]: (state: BowtieState, action: ReceiveCase): BowtieState => {
    return {
      ...state,
      inspectionEvaluations: {
        ...state.inspectionEvaluations,
        fetching: state.inspectionEvaluations.fetching.filter(caseNumber => caseNumber !== action.caseNumber),
        evaluationStatuses: {
          ...state.inspectionEvaluations.evaluationStatuses,
          [action.caseNumber]: action.evaluation
        }
      }
    };
  },

  [ACTION.FAIL_CASE]: (state: BowtieState, action: FailCase): BowtieState => {
    return {
      ...state,
      inspectionEvaluations: {
        ...state.inspectionEvaluations,
        fetching: state.inspectionEvaluations.fetching.filter(caseNumber => caseNumber !== action.caseNumber)
      }
    };
  },

  [ACTION.REQUEST_CONNECTED_CASES]: (state: BowtieState, action: RequestConnectedCases): BowtieState => {

    return {
      ...state,
      connectedCases: {
        ...state.connectedCases,
        fetching: {
          [action.caseNumber]: [
            ...(state.connectedCases.fetching?.[action.caseNumber] ?? []),
            action.connectionType
          ]
        }
      }
    };
  },

  [ACTION.RECEIVE_CONNECTED_CASES]: (state: BowtieState, action: ReceiveConnectedCases): BowtieState => {
    return {
      ...state,
      connectedCases: {
        ...state.connectedCases,
        fetching: {
          [action.caseNumber]: (state.connectedCases.fetching?.[action.caseNumber] ?? [])
            .filter(connectionType => connectionType !== action.connectionType)
        },
        connected: {
          ...state.connectedCases.connected,
          [action.caseNumber]: {
            ...(state.connectedCases.connected?.[action.caseNumber] ?? {}),
            [action.connectionType]: action.connected
          }
        }
      }
    };
  },

  [ACTION.FAIL_CONNECTED_CASES]: (state: BowtieState, action: FailConnectedCases): BowtieState => {
    return {
      ...state,
      connectedCases: {
        ...state.connectedCases,
        fetching: {
          [action.caseNumber]: (state.connectedCases.fetching?.[action.caseNumber] ?? [])
            .filter(connectionType => connectionType !== action.connectionType)
        }
      }
    };
  },

  [ACTION.CLEAN_DATA]: (state: BowtieState, action: CleanData): BowtieState => {
    return {
      ...state,
      ...INITIAL_BOWTIE_STATE,
      bowtieLayout: state.bowtieLayout,
      draftRequestId: state.draftRequestId,
      requestId: state.requestId
    };
  },

  [ACTION.REQUEST_CONNECT_DRAFT]: (state: BowtieState, action: RequestConnectDraft): BowtieState => {

    return state.fetchingConnections.some(c => c === action.caseNumber)
      ? state
      : {
        ...state,
        fetchingConnections: [
          ...state.fetchingConnections,
          action.caseNumber
        ],
        casesRequiringUpdate: [
          ...state.casesRequiringUpdate,
          action.caseNumber
        ]
      };
  },

  [ACTION.RECEIVE_CONNECT_DRAFT]: (state: BowtieState, action: ReceiveConnectDraft): BowtieState => {
    return {
      ...state,
      fetchingConnections: state.fetchingConnections.filter(c => c !== action.caseNumber)
    };
  },

  [ACTION.FAIL_CONNECT_DRAFT]: (state: BowtieState, action: FailConnectDraft): BowtieState => {
    return {
      ...state,
      fetchingConnections: state.fetchingConnections.filter(c => c !== action.caseNumber)
    };
  },

  [ACTION.REQUEST_BOWTIE_FAVOURITE]: (state: BowtieState, action: RequestBowtieFavourite): BowtieState => {
    return {
      ...state,
      creatingBowtieFavourite: true,
      bowtieFavouriteId: undefined
    };
  },

  [ACTION.RECEIVE_BOWTIE_FAVOURITE]: (state: BowtieState, action: ReceiveBowtieFavourite): BowtieState => {
    return {
      ...state,
      creatingBowtieFavourite: false,
      bowtieFavouriteId: action.bowtieFavouriteId
    };
  },

  [ACTION.INCREMENT_REQUEST_ID]: (state: BowtieState): BowtieState => {
    return {
      ...state,
      requestId: state.requestId + 1
    };
  }
};

export const reducer: Reducer<BowtieState> = (state: BowtieState = INITIAL_BOWTIE_STATE, action: any) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};
